export const HumanTaskBlockDocumentationMarkdown = `
## What does this block do?

The Human Task Block allows you to seamlessly integrate human workers into your workflows. Each time the block executes, it generates a task that appears in the [Task List](https://app.notablehealth.com/staff/app#/human-tasks), ready for assignment and completion.

Currently, three main task types are available:

1. **Custom:** Provides complete flexibility to design tasks with custom steps, data fields, and contextual resources.
2. **Dynamic Config:** Builds the task's subtasks at runtime from a JSON string. Useful when generating tasks from LLMs or upstream data.
3. **Submit Prior Authorization:** Streamlines the prior authorization process, potentially accelerating submission by 2-3x using AI assistance.

Additionally, the block supports two presentation modes for the workers who claim the tasks:

* **Standard:** Tasks open directly within the Notable platform.
* **Browser Extension:** Tasks open in a new browser tab and the task list loads in a sidebar using the Notable Sidekick Chrome extension.

The **Browser Extension** mode is particularly beneficial for tasks like prior authorizations and notice of admissions that require workers to perform tasks in external web portals.

## How does this block work overall?

Each task consists of discrete steps containing data fields (for displaying information or collecting user input) and work contexts (providing supplemental resources). Tasks have a configurable expiration time (default: 24 hours) and are automatically prioritized in the Task List based on their proximity to expiration – tasks expiring sooner appear at the top of the list. Workers must claim a task before beginning work, and all steps must be completed before the task can be finalized.

* **Task Generation:** Each time the block executes, a new task is created and added to the [Task List](https://app.notablehealth.com/staff/app#/human-tasks).
* **Configurable Expiration:** Tasks can have configurable expiration times (default: 24 hours).
  * The expiration time can be a static value or a dynamic variable passed from an upstream block (e.g., calculated based on the patient's next appointment date).
  * Expiration settings are configured in the "General" tab.
* **Automatic Prioritization:** Tasks are automatically prioritized in the Task List based on their expiration date, ensuring timely completion of urgent items.
* **Assignment Groups**: Tasks can be assigned to specific Assignment Groups (optional).
  * Assignment Groups are managed in the [Assignment Groups](https://app.notablehealth.com/staff/app#/assignment-groups) section.
  * Each assignment group is organization-specific and can include one or more users (internal or external) who have access to the organization.
  * Workers can be granted permissions to access only specific Assignment Groups, effectively routing different tasks to different workers from the same flow (e.g., by payer for authorizations, or by task type for referrals).
* **Tags:** Optional labels for organizing and finding tasks. Configure **Fixed** (static tag names) or **Dynamic** (a template expression that resolves to tag names when the task is created), in the **General** tab—or the **Settings** tab when using the newer Human Task layout. The [Task List](https://app.notablehealth.com/staff/app#/human-tasks) includes a Tags column and a tag filter.
* **Task Filtering:** Tasks in the Task List can be filtered by Organization, Agent, Flow, Assignment Group, or Tags for easier management.
* **Task Claiming:** Workers must claim a task before starting work. Only one worker can claim a task at a time, preventing concurrent work on the same item.

## Tags

Tags help you classify human tasks for operations and reporting. You can set them on the block so every new task receives those tags, adjust them later on an individual task where allowed, and use them when browsing the [Task List](https://app.notablehealth.com/staff/app#/human-tasks).

* **In the flow editor:** Open the **General** tab in the older layout, or the **Settings** tab in the newer Human Task layout. Use the **Tags** section to choose **Fixed** (a fixed list of tag names) or **Dynamic** (a template expression evaluated at task creation). Invalid tag names are rejected—see the inline helper text for naming rules.
* **In the Task List:** View tags in the Tags column and narrow results with the tag filter.

## Browser Extension requirements

To use the Browser Extension presentation mode, the following requirements must be met:

1. The workers must install the [**Sidekick Chrome Extension**](https://chromewebstore.google.com/detail/notable-auth-sidekick/hdgoooiiinahjoahcfaeihjogkplhfea?authuser=0&hl=en). 
   A. This is only available on Chrome browsers.
2. The task must have a URL work context to direct where to open the new browser tab and enable the Sidekick sidebar.

## Managing Staff Permissions for Task Access

Workers require **Flow Studio Single Sign-On (SSO) access** to access the Notable platform.
Once they have Notable access, the following permissions control task visibility, claiming, and completion:

1. **Expansive Access (View all Tasks):** Grants access to view tasks across any category within the organizations they have access to.
   1. Assign the "[Capability] - Human Task Worker" permission to the user.
2. **Restricted Access (Category-Limited):**  Limits access to tasks assigned to specific categories.
   1. Assign the "[Capability] - Category-limited Human Task Worker" permission to the user.
   2. ***Important:*** Ensure the user *does not* also have the Expansive permission, as it will override the restricted access.

## Custom Task Type

Each Custom task consists of discrete steps containing data fields (for displaying information or collecting user input) and work contexts (providing supplemental resources).

**Steps:**

* Represent discrete actions that the worker must complete, shown on the left side of the task UI.
* Provide step-by-step instructions with fields that either display static data or require input.
* All steps must be completed before the task can be marked complete.
* Each step can contain one or more Fields:
  * **Free-Form Text Content:** For detailed instructions or structured markdown content.
  * **Non-Editable Text:** For displaying reference data.
  * **Editable Text:** For collecting input from the worker.
  * **Single Select:** For selecting one option among predefined options.
  * **Multi Select:** For selecting one or more options among predefined options.
  * **Date:** Date picker for date selection

**Work Contexts:**

* Provide the context and resources the worker needs to complete the task, displayed as tabs on the right side of the task UI.
* Supported types of work contexts include:
  * **EHR:** Can write instructions on how to access EHR using markdown
  * **URL:** A URL link that will be embedded within an iFrame (e.g., https://www.notablehealth.com/). Note: Many websites block iFraming. If a website cannot be displayed, a button will appear allowing the user to open the URL in a new browser window.
  * **PDF:** File Reference to a PDF / TIFF document usually in the following format externalDocumentPacket:[documentID].
  * **Markdown Content:** Arbitrary markdown as context
  * **File:** A file reference to any file type, often using the same notation as PDFs but not limited to external document packets.
  * **Portal**: Specifically used for Submit Prior Authorization tasks. Ignore for Custom Task
  * **Voice Call:** Voice Call recording to render an audio link of the call to listen to.

**Use Cases**

Custom Task Types offer significant flexibility for a variety of use cases. Examples include:

1. **Data Verification:**
   1. A nurse reviewing AI-generated pre-visit preparation notes for accuracy.
   2. An auditor reviewing an AI-driven outbound voice call for quality and patient experience.
   3. Verifying data extracted from a referral fax.
2. **Automation Failure Handling:**
   1. Creating a task when an automated process (e.g., fax sending, FHIR-based patient matching) fails, requiring human intervention to resolve the issue.
3. **Automation Substitution:**
   1. Tasks can fill gaps when automated integrations are unavailable (e.g., manually updating a third-party tool like RCW).



These use cases can be combined as needed based on the specifics of your workflow.

### Clinical Question Assistant for Custom tasks

The Clinical Question Assistant is an optional tool for Custom tasks. It works in both Standard and Browser Extension presentation modes. Submit Prior Authorization tasks continue to include the assistant automatically; Dynamic Config tasks do not support it.

To configure the assistant for a Custom task:

1. Ensure that the flow is triggered with a Patient Order entity. An enabled assistant requires the Patient Order so it can retrieve fallback clinical context and associate questions with the correct order. If no Patient Order is available, the Human Task block fails before creating the task.
2. Open the Tools tab and enable **Clinical Question Assistant**. Disabling the tool hides it from workers but preserves its settings for later use.
3. Optionally configure **Data Context** with a flow template that resolves to JSON matching this shape:

   \`\`\`json
   {
     "documents": [
       { "documentId": "document-id", "text": "Clinical document text" }
     ],
     "labValues": [
       { "id": "lab-id", "value": "12", "testName": "Hemoglobin" }
     ],
     "relevantDocumentIds": ["document-id"]
   }
   \`\`\`

   All fields are optional, and the entire value may resolve to \`null\`. When Data Context is omitted, the assistant falls back to clinical documents associated with the Patient Order. If a Data Context template is configured, it must resolve to valid, non-empty JSON in the expected shape.
4. Optionally customize **Preamble**, **Output Requirements**, **Additional Instructions**, and **Model**. Preamble, Output Requirements, and Model display the effective runtime defaults without saving those defaults into the block. Editing a displayed default saves the edited value as an override; **Reset to default** removes the override. Additional Instructions has no non-empty default.

Workers access the task-level **Question Assistant** tab instead of launching the assistant from individual Custom subtask steps. Browser Extension tasks display the tab beside Steps and include best-effort context from the open portal. Standard tasks display it beside the task's work-context tabs and do not require the extension. The assistant displays a warning when no documents were explicitly configured, but Patient Order document fallback remains available. Submit Prior Authorization tasks retain their existing step-level assistant experience.

**Step-by-Step Configuration Guide**

1. **Choose Custom Task type from dropdown**
2. **Configure Task Fields (under "Task Fields" tab):**
   * Click + Add Step to create a new task step.
   * Enter a Step Title (e.g., "Find the Matching Patient in the EHR").
   * Add fields to the step by clicking + Add Field.
   * Select the Field Type (Free-Form Text Content, Non-Editable Text, Editable Text, Single Select, Multi Select, or others).
   * Enter a Field Label (e.g., "Patient Name") and optionally a Variable Name (e.g., patient_name) for downstream reference.
   * Optionally configure Inbound Data to pre-populate fields.
     1. For **Editable Text**: Pass a string value
        "John Smith"
     2. For **Single Select**: Pass the exact option value that matches one of the predefined options
        "Medicare"
     3. For **Multi Select**: Pass an array of exact option string values
        ["CPT123", "CPT456"]
     4. For **Date**: Pass an ISO 8601 formatted date string
        "2024-03-20"
   * Mark it as required (default) or make it optional.
3. **Configure Work Contexts (under "Work Contexts" tab):**
   * Click + Add Tab to add a new work context.
   * Choose a Content Type (EHR for markdown instructions, URL for webpages, PDF or File for documents, or Markdown Content for additional text).
4. **Set Expiration Duration (under "General" tab):**
   * Specify the task's expiration duration. The default is 24 hours, ensuring timely completion.
5. **Assign to Assignment Group (under "General" tab) if needed:**
   * Decide if you want to assign this to specific workers
    * If so, create a new Assignment Group or use an existing one in [Assignment Groups](https://app.notablehealth.com/staff/app#/assignment-groups).
    * Ensure the correct workers are added to that assignment group.
   * Ensure those workers only have the restricted worker perms (see Permissions section above) otherwise they will still see all tasks across assignment groups.
   * Assign that task to that assignment group by selecting it from the dropdown
6. **Optional — Configure Tags (under "Settings" tab, or under "General" in older layouts):**
   * Set **Fixed** tags or **Dynamic** tags as needed (see **Tags** above).
7. **Test the Task Configuration:**
   * Run a test flow to check that the task appears correctly in the Task List, complete with all steps and work contexts.
8. **Simulate Task Completion:**
   * Complete the task by filling in the fields, then confirm that output variables (e.g., \`{{human_task.data.patient_name}}\`) are passed to downstream blocks correctly.
9. **Verify Expiration and Priority:**
   * Ensure that tasks expire as configured and that their order in the Task List reflects their expiration times.

## Dynamic Config Task Type

The Dynamic Config task type lets you define subtasks at runtime via a JSON string. This is ideal when subtasks are programmatically generated (e.g., by an LLM) from upstream context.

Key points:

* The block's 'taskType' must be 'DynamicConfig'.
* Provide 'config.dynamicConfig' as a JSON string whose root is an array of steps. Do not provide a full block object; only the steps array.
* 'workContexts', 'expirationDuration', optional 'categoryId', and optional 'tags' (Fixed or Dynamic, same as other task types) are still configured on the block as usual.

Schema expectations (root is an array of 'HumanTaskStep'):

* HumanTaskStep: '{ id: string, title: string, fields: HumanTaskField[], isVisibleConditions?: Condition[] }'
* HumanTaskField (discriminated by 'fieldType'):
  * 'ContentMarkdown': '{ id, fieldType: "ContentMarkdown", content }'
  * 'ReadOnlyValue': '{ id, fieldType: "ReadOnlyValue", label, value, helperText? }'
  * 'EditableValue': '{ id, fieldType: "EditableValue", label, variableName, initialValue, helperText? }'
  * 'ImageUpload': '{ id, fieldType: "ImageUpload", label, variableName, helperText? }'
  * 'FileUpload': '{ id, fieldType: "FileUpload", label, variableName, helperText? }'
  * 'DateOnlyValue': '{ id, fieldType: "DateOnlyValue", label, variableName, initialValue, helperText? }'
  * 'SingleOptionValue': '{ id, fieldType: "SingleOptionValue", label, options[], variableName, initialValue, optionsVariant?, dynamicOptions? }'
  * 'MultiOptionValue': '{ id, fieldType: "MultiOptionValue", label, options[], variableName, initialValue?, optionsVariant?, dynamicOptions? }'

Validation rules (common pitfalls):

* 'EditableValue.initialValue' is required (string). Use "" for unset; null is invalid.
* 'DateOnlyValue.initialValue' must resolve to a valid YYYY-MM-DD string. Use "" if unset; all other formats are rejected.
* 'SingleOptionValue.initialValue' must be a string and should match one of the option values.
* 'ReadOnlyValue.value' and 'ContentMarkdown.content' are required strings.

Minimal example (what to put into 'config.dynamicConfig'):

    [
      {
        "id": "6f1f85a1-9f1f-4b3a-8c2a-6f4a6c2b9c01",
        "name": "Review",
        "steps": [
          {
            "id": "intro",
            "title": "Intro",
            "fields": [
              { "id": "hdr", "fieldType": "ContentMarkdown", "content": "## Please review" },
              { "id": "status", "fieldType": "SingleOptionValue", "label": "Status", "options": [
                { "id": "ok", "label": "OK", "value": "OK" },
                { "id": "fix", "label": "Needs Fix", "value": "FIX" }
              ], "variableName": "reviewStatus", "initialValue": "OK", "dynamicOptions": "", "optionsVariant": "Static" }
            ]
          }
        ]
      }
    ]


Troubleshooting:

* Error 'Invalid JSON in dynamic config' → your 'dynamicConfig' is not a valid JSON string.
* Error 'parsedConfig.forEach is not a function' → your JSON root is not an array; pass just '[ { ...subtask } ]', not a full block.
* Validation error pointing at 'initialValue' → ensure required 'initialValue' fields are present and strings (use "" instead of null).

## Submit Prior Authorization Task Type

A Submit Prior Authorization task is designed to accelerate prior authorization submissions (potentially 2-3x faster) using the AI-powered Sidekick browser extension. This task type opens the configured payer web portal (e.g., RadMD, UHC) in a new browser window, alongside the AI Sidekick Chrome extension. This task type requires the Chrome browser.

The Sidekick extension comes pre-loaded with three main steps:

1. **Submit the Authorization:** This step contains all the standard information needed for authorization submission, including patient details, procedure and diagnosis information, rendering provider information, and more. Each field has a "click to copy" button for easy data transfer. An AI assistant is also available to answer complex clinical questions. By clicking "Get Help with a Question", you can paste questions directly from the portal, along with the provided Answer Options, and receive a response. The AI takes into account all the data context provided during the block configuration to generate answers.
2. **Upload Clinical Packet:** Many portals require a clinical document packet at the end of the submission. This step surfaces a recommended packet (based on the block configuration) and allows you to edit the packet by deselecting unneeded documents and regenerating a new packet. You can download and upload the final packet to the portal.
3. **Document the Status:** This step provides fields to document the final authorization status as determined by the portal. It also provides the ability to upload a screenshot of the portal or a PDF download of the final page for auditing purposes. This documented information is available as an output of the block and can be exported back to the EHR. The authorization status itself is always required, but the additional detail fields (authorization dates, tracking number, and verification file uploads) can be made optional per-block via the "Require authorization status details" setting — useful for workflows that don't need to capture those details on every submission.

**Step-by-Step Configuration Guide**

1. **Choose Submit Prior Authorization Task type from dropdown**
2. **Configure Patient Order ID**
   * Usually comes from a prior Get Patient Order block using \`{{patientOrder.data.id}}\` syntax
   * This will populate all the order information in the AI Sidekick side bar
3. **Configure Rendering Facility (optional)**
   * Pass in JSON object with the following information
     1. NPI
     2. Phone
     3. Main Address
   * Usually comes from a Reference Table with all Rendering Facility mappings that is then filtered for this specific facility
4. **Configure Contact Information (optional)**
   * Typically just the Patient Phone Number e.g. \`{{patient.data.phoneNumber}}\`
5. **Configure Clinical Documents Packet (optional)**
   * This is a file reference to the packet that will be the ‘recommended’ packet in Step 2 of the task
6. **Configure Sidekick Data Context**
   * This is the data context that the AI Sidekick Q/A assistant will use to answer questions
   * Each field is optional
   * Required to be in the format:
\`\`\`
{
   "documents": [], // array of document text (e.g. from Entity Data Store block)
   "labValues": [], // array of FHIR lab values
   "relevantDocumentIds": [] // array of document IDs to pre-select and prioritize for packet assembly
}
\`\`\`

7. **Configure Model**
   * Recommend conducting evaluations to see which model performs best and using that
8. **Configure Authorization Status Details Requirement**
   * The "Require authorization status details" toggle controls whether the authorization dates, tracking number, and verification file upload fields on the "Document the Status" step must be filled in before the task can be completed.
   * Defaults to on (required). Turn it off to make those detail fields optional so workers can complete the task by recording only the authorization status. Useful when your workflow only needs the status outcome and captures the supporting details elsewhere (or not at all).
   * The authorization status field itself is always required regardless of this setting.
9. **Configure Work Contexts (under "Work Contexts" tab):**
   * Select the Portal to be opened
10. **Set Expiration Duration (under "Settings" tab):**
    * Specify the task's expiration duration. The default is 24 hours, ensuring timely completion.
11. **Optional — Configure Tags (under "Settings" tab, or under "General" in older layouts):**
    * Use tags to make tasks easier to filter, organize, and report on in the Task List.
    * **Fixed Tags:** Add static values when you want every generated task to carry the same label (for example, \`prior-auth\` or a payer name).
    * **Dynamic Tags:** Reference variables when the tag should be populated at runtime from flow data (for example, patient-, payer-, or facility-specific values).
    * Prefer concise, consistent tag names so downstream filtering and operational reporting remain reliable.
12. **Assign to Assignment Group (under "Settings" Tab) if needed:**
    * Decide if you want to assign this to specific workers
    * If so, create a new Assignment Group or use an existing one in [Assignment Groups](https://app.notablehealth.com/staff/app#/assignment-groups).
    * Ensure the correct workers are added to that assignment group.
    * Ensure those workers only have the restricted worker perms (see Permissions section above) otherwise they will still see all tasks across assignment groups.
    * Assign that task to that assignment group by selecting it from the dropdown
13. **Test the Task Configuration:**
    * Run a test flow to check that the task appears correctly in the Task List, complete with all steps and work contexts.
14. **Simulate Task Completion:**
    * Complete the task by filling in the fields, then confirm that output variables (e.g., \`{{human_task.data.patient_name}}\`) are passed to downstream blocks correctly.
15. **Verify Expiration and Priority:**
    * Ensure that tasks expire as configured and that their order in the Task List reflects their expiration times.

## Best practices

* Clear Instructions: Use specific and concise Step Titles and Field Labels to guide workers effectively.
* Use Meaningful Variable Names: Assign intuitive variable names (e.g., patient_name) to facilitate data reference in subsequent blocks.
* Provide Sufficient Context: Include all necessary Work Contexts (URLs, PDFs, markdown) so workers have the resources needed to complete the task.
* Leverage Mock Mode: Test with "Mock output for test run" enabled to ensure the task configuration works correctly before deploying live.
* Monitor and Refine: Regularly review tasks in the Task List and adjust expiration durations, steps, and work contexts as needed.
* Use Tags Consistently: Agree on tag names across flows (e.g., payer, priority, or workstream) so filters and the Tags column stay meaningful for your team.

## How to Get More Help

FlowAI can help you clarify how Human Task block works and how to use it effectively.
It can also help with configuring the block and troubleshooting issues.

## Block Limitations

### Task State and Lifecycle Limitations

* **Irreversible Terminal States:** Once a task reaches the "Expired" or "Complete" state, it cannot be claimed, modified, or reassigned. The only way to retry is to create a new task.
* **No Direct Reassignment:** Tasks cannot be directly reassigned from one worker to another. The current worker must first unclaim the task, returning it to the Pending state, before another worker can claim it.
* **Single Worker Constraint:** Only one worker can claim a task at a time. Attempting to claim an already-claimed task will fail.

### Task Modification and Configuration Limitations

* **No Editing Steps, Fields, or Work Contexts After Creation:** Once a task is created, its steps, fields, work contexts, and expiration cannot be changed. Tags are an exception: permitted users can add or remove tags via **Manage Tags** on the task detail page without altering the task’s underlying configuration.
* **No Task Deletion:** Tasks cannot be deleted from the system. They can only be expired or completed.
* **Variable Name Restrictions:** Field variable names must follow strict naming conventions:
  * Must start with a letter
  * Can only contain letters, numbers, and underscores
  * Example: \`patient_name\`, \`authNumber\`, \`status1\`

### Browser Extension and Work Context Limitations

* **Chrome Browser Only:** The Browser Extension presentation mode requires Chromium-based browsers, such as Chrome, Edge, Brave.
* **Browser Extension URL Requirement:** Tasks using Browser Extension mode must include at least one URL work context to direct where the new browser tab should open and enable the Sidekick sidebar.

### Submit Prior Authorization Task Limitations

* **Limited Portal Support:** Only specific payer portals are supported:
  * Availity
  * Carelon
  * CareCore
  * Cohere
  * MedSolutions
  * RadMD
  * UnitedHealthcare (UHC)
* **Patient Order Required:** Submit Prior Authorization tasks require a valid Patient Order ID from a prior Get Patient Order block.
* **Chrome Browser Required:** Like other Browser Extension mode tasks, prior authorization tasks require the Chrome browser with the Sidekick extension installed.
* **Document Summarization Concurrency:** The system limits concurrent document summarization requests to 3 at a time for performance reasons.

### Data and Field Limitations

* **Initial Value Requirements (Dynamic Config):**
  * \`EditableValue.initialValue\` must be a string (use "" for empty, not null)
  * \`DateOnlyValue.initialValue\` must resolve to a valid YYYY-MM-DD string (or "" when unset). All other formats are rejected.
  * \`SingleOptionValue.initialValue\` must match one of the predefined option values exactly
  * \`MultiOptionValue.initialValue\` must be an array of strings matching option values
* **JSON Configuration Validation:** Dynamic Config tasks require properly formatted JSON. Invalid JSON will cause the task creation to fail with an error.
* **Step Completion Required:** All steps in a task must be completed before the task can be marked complete. Partial completion is not allowed.

### Permission and Access Limitations

* **No Mixed Permission Levels:** Task Workers cannot have both "Expansive Access" and "Category-Limited" permissions. The Expansive permission will always override restricted access if both are assigned.
* **SSO Requirement:** All workers require Flow Studio Single Sign-On (SSO) access to access tasks.
* **Organization Boundaries:** Tasks are scoped to specific organizations. Workers can only access tasks for organizations they have access to.

### Performance and Workflow Limitations

* **Task Expiration Drives Workflow Timeout:** The workflow waits for task completion based on the configured expiration duration. If a task expires, the workflow continues with an expiration signal.
* **Fixed Prioritization:** Task prioritization is automatically based on expiration time and cannot be manually adjusted. Tasks expiring sooner always appear at the top of the list.

`;
